import { STREAM_COALESCE_MS } from '@ctcells/core'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { eventHistoryKey, type EventHistoryArgs } from '../queries/admin.ts'

import type { SubscribeFn } from './useStreamBridge.ts'

/**
 * 구독 프레임 한 건이 **한 작업의 이벤트 원장(`/history?event_type=event|batch_record&batch_id=` ·
 * `useEventHistory`)을 다시 받을 까닭**이 되는가. 지금 쓰는 곳은 상시 작업 줄의 이벤트 칸(`StripEvents`)이다.
 * ⚠ 2026-10-08(v0.9.4)부터 배치 전이 · 발송 · 실행 관측은 `batch_record` 에 쌓이고, 모듈 상태 전이는 `event` 에 남는다 —
 *   아래 갈래가 둘 다 덮는다(`batch_status` · `module_status`).
 *
 * 🔴 **원장(`/history?event_type=event`) 행이 어디서 생기는지 실물로 보고 고른 갈래다**
 *    (2026-09-28 로컬 실측 · 한 배치 166행). 162행이 `lifecycle / module_state_change` —
 *    **모듈 상태가 바뀔 때** 한 줄씩 쌓인다. 나머지는 `dispatch_send` · `execution_observed`.
 *
 * | 프레임 | 다시 받는가 | 까닭 |
 * | --- | --- | --- |
 * | `module_status` | **`payload.state` 가 직전과 다를 때만** | 원장의 대부분이 그 전이다. 1Hz 심장박동은 거른다 |
 * | `batch_status` · `sample_*` | 이 작업 것일 때 | 작업 전이 · 단계 표지 |
 * | `alarm` | 늘 | 알람은 드물고 원장에 남을 수 있다 |
 * | `batch_progress` | **아니다** | 실행 중 1Hz 로 온다. 원장 행을 만들지 않는 것이 대부분이다 |
 *
 * ⚠ **어느 모듈 전이가 «이 작업의» 행인지는 서버가 가른다**(`batch_id` 필터). 여기서 모듈 ↔ 작업을
 *   추론하지 않는다 — 다른 작업의 모듈 전이로 다시 받으면 같은 본문이 올 뿐이다(틀리는 쪽이 아니라
 *   한 번 더 묻는 쪽으로 넘어진다).
 * ⚠ 빠뜨린 갈래가 있으면 **다음 신호 때 따라온다** — 늦는 것이지 틀리는 것이 아니다.
 */
export type BatchEventsFrameMemo = Map<string, string>

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function textOf(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined
}

export function batchEventsShouldRefresh(
  raw: unknown,
  batchId: string,
  memo: BatchEventsFrameMemo,
): boolean {
  const frame = asRecord(raw)
  if (frame === null) return false
  const payload = asRecord(frame['payload'])
  const frameBatch = textOf(frame['batch_id']) ?? textOf(payload?.['batch_id'])

  switch (frame['type']) {
    case 'module_status': {
      const moduleId = textOf(frame['module_id']) ?? textOf(payload?.['module_id'])
      const state = textOf(payload?.['state'])
      if (moduleId === undefined || state === undefined) return false
      const previous = memo.get(moduleId)
      memo.set(moduleId, state)
      // 첫 프레임은 «기준»이다 — 전이가 아니다(펼친 순간 스냅샷을 이미 받는다)
      return previous !== undefined && previous !== state
    }
    case 'batch_status':
    case 'sample_lifecycle':
    case 'sample_progress':
      return frameBatch === batchId
    case 'alarm':
      return true
    default:
      return false
  }
}

/**
 * 구독 → 그 작업의 이벤트 원장 다시 받기. **무효화 하나**다 — 캐시에 행을 지어 넣지 않는다.
 *
 * 🔴 **구독 프레임을 «행으로» 만들지 않는다.** 구독 본문(`module_status` · `batch_progress` …)은
 *    원장 행과 모양이 다르다. 두 모양을 한 목록에 섞으면 같은 사건이 두 번, 다른 얼굴로 선다.
 *    원장이 SSOT 이고 구독은 「새 행이 생겼을 수 있다」는 신호다.
 * 🔴 묶음 창(`STREAM_COALESCE_MS`)으로 모은다. **폴링이 아니다** — 프레임이 없으면 아무 일도 없다.
 * 🔴 stale → 다시 붙음 전이에서 **한 번** 다시 받는다(끊긴 동안 쌓인 행). 첫 마운트에는 안 친다 —
 *    `useEventHistory` 가 이미 받고 있어서, 치면 진행 중 요청을 취소하고 한 번 더 나간다.
 * ⚠ `packages/ui` 는 쿼리 라이브러리를 직접 못 쓴다(린트 규칙) — 그래서 이 훅이 여기 있다.
 *   `subscribe` 를 인자로 받는 것은 `useStreamBridge` 와 같은 까닭이다(api 가 realtime 을 모른다).
 */
export function useBatchEventsRefresh(
  subscribe: SubscribeFn,
  args: EventHistoryArgs & { batch_id: string },
  stale: boolean,
): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    const queryKey = eventHistoryKey(args)
    const memo: BatchEventsFrameMemo = new Map()
    let timer: ReturnType<typeof setTimeout> | null = null
    const off = subscribe('events', (raw) => {
      if (!batchEventsShouldRefresh(raw, args.batch_id, memo) || timer !== null) return
      timer = setTimeout(() => {
        timer = null
        void queryClient.invalidateQueries({ queryKey, exact: true })
      }, STREAM_COALESCE_MS)
    })
    return () => {
      off()
      if (timer !== null) clearTimeout(timer)
    }
  }, [args, queryClient, subscribe])

  const wasStale = useRef(stale)
  useEffect(() => {
    if (wasStale.current && !stale)
      void queryClient.invalidateQueries({ queryKey: eventHistoryKey(args), exact: true })
    wasStale.current = stale
  }, [args, queryClient, stale])
}
